/**
 * @fileoverview Zod schemas and types for Universal Transactions feature.
 * @module features/universal-transactions/schemas/universal-transactions
 */

import { z } from 'zod'

/**
 * Parse configuration for transforming CSV fields.
 */
export const ParseConfigSchema = z.object({
  type: z.enum(['date', 'number']),
  formats: z.array(z.string()).optional(),
  decimal: z.string().optional(),
  thousands: z.string().optional(),
})
export type ParseConfig = z.infer<typeof ParseConfigSchema>

/**
 * Column mapping from CSV header to canonical field.
 */
export const ColumnMappingSchema = z.object({
  from: z.union([z.string(), z.array(z.string())]).optional(),
  parse: ParseConfigSchema.optional(),
  default: z.string().optional(),
  concat: z.string().optional(),
})
export type ColumnMapping = z.infer<typeof ColumnMappingSchema>

/**
 * Conditional rule to derive transaction direction.
 */
export const DirectionConditionalSchema = z.object({
  when: z.object({
    col: z.union([z.string(), z.array(z.string())]).optional(),
    lt: z.number().optional(),
    gt: z.number().optional(),
    eq: z.union([z.string(), z.number()]).optional(),
  }),
  then: z.enum(['debit', 'credit']),
})
export type DirectionConditional = z.infer<typeof DirectionConditionalSchema>

/**
 * Mapping for deriving direction field.
 */
export const DirectionMappingSchema = z.object({
  derive: z.object({
    if: z.array(DirectionConditionalSchema),
    else: z.enum(['debit', 'credit']),
  }),
})
export type DirectionMapping = z.infer<typeof DirectionMappingSchema>

/**
 * Mapping definition object that describes canonical fields and optional extras/filters.
 */
export const MappingDefinitionSchema = z.object({
  version: z.number(),
  columns: z.object({
    txn_date: ColumnMappingSchema.optional(),
    amount: ColumnMappingSchema.optional(),
    direction: DirectionMappingSchema.optional(),
    currency: ColumnMappingSchema.optional(),
    title: ColumnMappingSchema.optional(),
    counterparty_name: ColumnMappingSchema.optional(),
    status: ColumnMappingSchema.optional(),
  }),
  extras: z.array(z.string()).optional(),
  row_filters: z.array(z.unknown()).optional(),
})
export type MappingDefinition = z.infer<typeof MappingDefinitionSchema>

/**
 * Request to create/update mapping configuration.
 */
export const MappingConfigRequestSchema = z.object({
  name: z.string().min(1),
  sampleHeader: z.array(z.string()),
  mapping: MappingDefinitionSchema,
})
export type MappingConfigRequest = z.infer<typeof MappingConfigRequestSchema>

/**
 * Response for mapping configuration metadata.
 */
export const MappingConfigResponseSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  createdAt: z.string(),
  // BE also returns updatedAt; keep it optional here to remain tolerant if not provided
  updatedAt: z.string().optional(),
})
export type MappingConfigResponse = z.infer<typeof MappingConfigResponseSchema>

/**
 * Detailed mapping config (GET by id)
 */
export const MappingConfigDetailSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  createdAt: z.string(),
  updatedAt: z.string(),
  sampleHeader: z.array(z.string()),
  mapping: MappingDefinitionSchema,
})
export type MappingConfigDetail = z.infer<typeof MappingConfigDetailSchema>

/**
 * Response for CSV preview.
 */
export const PreviewResponseSchema = z.object({
  header: z.array(z.string()),
  rows: z.array(z.record(z.string(), z.string())),
  totalStored: z.number(),
  suggestedMappingId: z.string().optional().nullable(),
  suggestedMappingName: z.string().optional().nullable(),
})
export type PreviewResponse = z.infer<typeof PreviewResponseSchema>

/**
 * Response after CSV ingestion.
 */
export const IngestionResponseSchema = z.object({
  batchRef: z.string(),
  totalRows: z.number(),
  inserted: z.number(),
  updated: z.number(),
  skipped: z.number(),
  duplicates: z.number(),
  parseErrors: z.number(),
  validationErrors: z.number(),
  errorSamples: z.array(z.string()),
})
export type IngestionResponse = z.infer<typeof IngestionResponseSchema>

/**
 * Canonical Universal Transaction entity (for list/views).
 */
export const UniversalTransactionSchema = z.object({
  id: z.string(),
  orgId: z.string().optional(),
  txnDate: z.string(),
  originalAmount: z.number().optional(),
  originalCurrency: z.string().optional(),
  originalSignedAmount: z.number().optional(),
  reportingAmount: z.number().optional(),
  reportingCurrency: z.string().optional(),
  reportingSignedAmount: z.number().optional(),
  amount: z.number().optional(),
  direction: z.enum(['DEBIT', 'CREDIT']),
  signedAmount: z.number().optional(),
  currency: z.string().optional(),
  title: z.string().nullable().optional(),
  counterpartyName: z.string().nullable().optional(),
  fingerprint: z.string().optional(),
  rawSignature: z.string().optional(),
  extras: z.record(z.string(), z.unknown()).nullable().optional(),
  status: z.enum(['POSTED', 'PENDING']).nullable().optional(),
  accountId: z.string().nullable().optional(),
  accountIban: z.string().nullable().optional(),
  category: z.unknown().nullable().optional(),
  vatApplicable: z.boolean().nullable().optional(),
  reverseCharge: z.boolean().nullable().optional(),
  vatRate: z.number().nullable().optional(),
  originalVatAmount: z.number().nullable().optional(),
  reportingVatAmount: z.number().nullable().optional(),
  originalNetAmount: z.number().optional(),
  reportingNetAmount: z.number().optional(),
  reviewed: z.boolean().optional(),
  notes: z.string().nullable().optional(),
  invoiceStatus: z.string().optional(),
  costLinkingRequirement: z.string().optional(),
  createdAt: z.string(),
  linkedIndirectCostCount: z.number().optional(),
  linkingRequirementSatisfied: z.boolean().optional(),
})
export type UniversalTransaction = z.infer<typeof UniversalTransactionSchema>

/**
 * Generic page schema for list endpoints (if needed later).
 */
export function PageSchema<T extends z.ZodTypeAny>(
  itemSchema: T
): z.ZodObject<{
  content: z.ZodArray<T>
  totalElements: z.ZodOptional<z.ZodNumber>
  totalPages: z.ZodOptional<z.ZodNumber>
  size: z.ZodOptional<z.ZodNumber>
  number: z.ZodOptional<z.ZodNumber>
}> {
  return z.object({
    content: z.array(itemSchema),
    totalElements: z.number().optional(),
    totalPages: z.number().optional(),
    size: z.number().optional(),
    number: z.number().optional(),
  })
}
export type Page<T> = {
  content: T[]
  totalElements?: number
  totalPages?: number
  size?: number
  number?: number
}

/**
 * Category delta in a reclassify change item.
 */
export const ReclassifyCategoryDeltaSchema = z.object({
  from: z.union([z.string(), z.number()]).nullable(),
  fromName: z.string().nullable(),
  to: z.number().nullable(),
  toName: z.string().nullable(),
})
export type ReclassifyCategoryDelta = z.infer<typeof ReclassifyCategoryDeltaSchema>

/**
 * VAT delta in a reclassify change item.
 */
export const ReclassifyVatDeltaSchema = z.object({
  fromApplicable: z.boolean().nullable(),
  toApplicable: z.boolean().nullable(),
  fromRate: z.number().nullable(),
  toRate: z.number().nullable(),
  fromReverseCharge: z.boolean().nullable(),
  toReverseCharge: z.boolean().nullable(),
})
export type ReclassifyVatDelta = z.infer<typeof ReclassifyVatDeltaSchema>

/**
 * Single change item describing what changed for a transaction.
 */
export const ReclassifyChangeItemSchema = z.object({
  transactionId: z.string(),
  title: z.string().nullable(),
  counterpartyName: z.string().nullable(),
  category: ReclassifyCategoryDeltaSchema.nullable(),
  vat: ReclassifyVatDeltaSchema,
})
export type ReclassifyChangeItem = z.infer<typeof ReclassifyChangeItemSchema>

/**
 * Reclassify batch report schema (enhanced)
 */
export const ReclassifyReportSchema = z.object({
  scanned: z.number(),
  updated: z.number(),
  skipped: z.number(),
  changes: z.array(ReclassifyChangeItemSchema),
})
export type ReclassifyReport = z.infer<typeof ReclassifyReportSchema>
